import { useMemo, type ReactNode } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { LoadSnapshot } from "@/services/api/types";

interface SnapshotTableProps {
  rows: LoadSnapshot[];
  /** Optional header for the trailing (per-row action) column. */
  trailingHeader?: string;
  /** Renders the trailing column cell for a row (e.g. an action button). */
  renderTrailing?: (row: LoadSnapshot) => ReactNode;
  /** Message shown when there are no rows. Defaults to "No results". */
  emptyLabel?: string;
}

/**
 * Non-paginated snapshot table sharing the /admin/loads column format: Record,
 * Delivery date, Arrival time, Customer, Destination, Postcode, Haulier, Pallet
 * count — plus one trailing column supplied by the caller (depot reference,
 * dispatch button, etc.). Used by the depot and loadboard portals.
 */
export function SnapshotTable({
  rows,
  trailingHeader,
  renderTrailing,
  emptyLabel,
}: SnapshotTableProps) {
  const { t } = useTranslation();
  const columns = useMemo<ColumnDef<LoadSnapshot>[]>(() => {
    const base: ColumnDef<LoadSnapshot>[] = [
      { accessorKey: "load_ref", header: t("admin.record") },
      {
        accessorKey: "delivery_date",
        header: t("admin.deliveryDate"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "arrival_time",
        header: t("admin.arrivalTime"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "customer_name",
        header: t("admin.customer"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "destination_address",
        header: t("admin.destinationAddress"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "postcode",
        header: t("admin.postcode"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "haulier_name",
        header: t("admin.haulier"),
        cell: ({ getValue }) => (getValue() ? String(getValue()) : "—"),
      },
      {
        accessorKey: "total_pallet_count",
        header: t("admin.palletCount"),
        cell: ({ getValue }) => String(getValue() ?? "—"),
      },
    ];
    if (renderTrailing) {
      base.push({
        id: "trailing",
        header: trailingHeader ?? "",
        cell: ({ row }) => renderTrailing(row.original),
      });
    }
    return base;
  }, [t, trailingHeader, renderTrailing]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyLabel ?? "No results"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
